import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withViewTransitions
} from '@angular/router';
import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { MyPreset } from './prime-ng-theme';
import { provideToastr } from 'ngx-toastr';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { API_CONFIG, dateTransformInterceptor } from '@zayan-erp/shared';
import { CrmPublicImageService } from '@zayan-erp/crm';
import { CUI_IMAGE_UPLOADER, CuiImageUploader } from '@zayan-erp/crm-ui';
import { InterceptorService } from './shared/interceptors/interceptor.service';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { appEffects } from './store/app.effects';
import { appReducers } from './store/app.reducers';
import { environment } from '../environments/environment';
// import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideLottieOptions } from 'ngx-lottie';
// The light build drops the expression engine, which needs eval and is blocked by the CSP in customHttp.yml.
import player from 'lottie-web/build/player/lottie_light';
import { ConfirmationService } from 'primeng/api';
import { provideRealtime } from '@zayan-erp/realtime';
import { realtimeTokenProvider } from './realtime/realtime-token.provider';
import { CrmNoticesRealtimeService } from './realtime/crm-notices-realtime.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions(), withPreloading(PreloadAllModules)),
    provideHttpClient(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      closeButton: true,
      progressBar: false,
      progressAnimation: 'increasing',
      tapToDismiss: false
    }),
    provideHttpClient(withInterceptors([dateTransformInterceptor]), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'MMM d, y, h:mm a' } },
    provideEffects(appEffects),
    provideStore(appReducers),
    // provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    {
      provide: API_CONFIG,
      useValue: {
        baseUrl: environment.baseUrl,
        baseUrlV2: environment.baseUrlV2,
        recaptchaSiteKey: environment.recaptchaSiteKey
      }
    },
    provideLottieOptions({ player: () => player }),
    provideAppInitializer(() => {
      // Injected for its constructor: it subscribes to the realtime feed at bootstrap, so CRM
      // notices reach the user on every page rather than only where a list is open.
      inject(CrmNoticesRealtimeService);
    }),
    // One realtime connection for the app (/realtime-hub). The legacy SignalR services are separate.
    provideRealtime(
      {
        hubUrl: environment.realtimeHubUrl,
        enabled: environment.realtimeEnabled,
        verbose: !environment.production
      },
      realtimeTokenProvider
    ),
    // Images picked in a rich-text editor go to the public bucket; the saved HTML keeps their URL.
    {
      provide: CUI_IMAGE_UPLOADER,
      useFactory: (): CuiImageUploader => {
        const images = inject(CrmPublicImageService);
        return file => images.upload(file);
      }
    }
  ]
};
